import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit2, X, Check, AlertCircle, ChevronDown, ChevronUp, Filter, Tag, Link2, ArrowUp, ArrowDown, Sparkles, Loader2 } from 'lucide-react';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { API, useAuth } from '../App';

const PROPERTY_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'number', label: 'Number' },
  { value: 'predefined_list', label: 'Dropdown (Select)' },
  { value: 'boolean', label: 'Yes/No Toggle' }
];

const LISTING_TYPES = [
  { value: 'sale', label: 'Sell' },
  { value: 'rent', label: 'Rent' },
  { value: 'trade', label: 'Trade' },
  { value: 'service', label: 'Service' },
  { value: 'yard_sale', label: 'Yard Sale' },
  { value: 'auction', label: 'Auction' }
];

// Property Form Component
const PropertyForm = ({ 
  property, 
  onPropertyChange, 
  onSave, 
  onCancel, 
  isEditing, 
  categorySubcategories,
  allProperties,
  newOption,
  setNewOption,
  newSuggestion,
  setNewSuggestion,
  categoryName,
  token
}) => {
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiModalMode, setAiModalMode] = useState('options'); // 'options' or 'suggestions'
  const [aiContext, setAiContext] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);

  const handleFieldChange = useCallback((field, value) => {
    onPropertyChange({ ...property, [field]: value });
  }, [property, onPropertyChange]);

  const handleNameChange = useCallback((lang, value) => {
    onPropertyChange({ 
      ...property, 
      name: { ...property.name, [lang]: value } 
    });
  }, [property, onPropertyChange]);

  const handlePlaceholderChange = useCallback((lang, value) => {
    onPropertyChange({ 
      ...property, 
      placeholder: { ...property.placeholder, [lang]: value } 
    });
  }, [property, onPropertyChange]);

  const addOption = useCallback(() => {
    if (!newOption.trim()) return;
    onPropertyChange({
      ...property,
      options: [...(property.options || []), newOption.trim()]
    });
    setNewOption('');
  }, [property, newOption, onPropertyChange, setNewOption]);

  const removeOption = useCallback((index) => {
    onPropertyChange({
      ...property,
      options: property.options.filter((_, i) => i !== index)
    });
  }, [property, onPropertyChange]);

  // Suggestion management (for text input autocomplete)
  const addSuggestion = useCallback(() => {
    if (!newSuggestion?.trim()) return;
    const currentSuggestions = property.suggestions || [];
    if (currentSuggestions.includes(newSuggestion.trim())) {
      toast.error('This suggestion already exists');
      return;
    }
    onPropertyChange({
      ...property,
      suggestions: [...currentSuggestions, newSuggestion.trim()].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    });
    setNewSuggestion('');
  }, [property, newSuggestion, onPropertyChange, setNewSuggestion]);

  const removeSuggestion = useCallback((index) => {
    onPropertyChange({
      ...property,
      suggestions: property.suggestions.filter((_, i) => i !== index)
    });
  }, [property, onPropertyChange]);

  const toggleSubcategory = useCallback((subcategory) => {
    const current = property.subcategories || [];
    const updated = current.includes(subcategory)
      ? current.filter(s => s !== subcategory)
      : [...current, subcategory];
    onPropertyChange({ ...property, subcategories: updated });
  }, [property, onPropertyChange]);

  const toggleListingType = useCallback((listingType) => {
    const current = property.listing_types || [];
    const updated = current.includes(listingType)
      ? current.filter(t => t !== listingType)
      : [...current, listingType];
    onPropertyChange({ ...property, listing_types: updated });
  }, [property, onPropertyChange]);

  const availableForDependency = allProperties.filter(p => p.id !== property.id && (p.type === 'boolean' || p.type === 'predefined_list'));

  const handleDependencyChange = useCallback((field, value) => {
    const currentDep = property.depends_on || {};
    if (field === 'property_key') {
      const depProp = allProperties.find(p => p.key === value);
      onPropertyChange({
        ...property,
        depends_on: value ? { property_key: value, value: depProp?.type === 'boolean' ? true : '' } : null
      });
    } else {
      onPropertyChange({
        ...property,
        depends_on: { ...currentDep, [field]: value }
      });
    }
  }, [property, allProperties, onPropertyChange]);

  const selectedDepProperty = allProperties.find(p => p.key === property.depends_on?.property_key);

  // AI Generation functions
  const generateWithAI = async () => {
    if (!aiContext.trim()) {
      toast.error('Please provide context for the AI');
      return;
    }

    setAiLoading(true);
    try {
      const existingValues = aiModalMode === 'options' 
        ? (property.options || []) 
        : (property.suggestions || []);
      
      const response = await axios.post(`${API}/admin/ai/generate-property-values`, {
        property_name: property.name?.en || property.key || 'property',
        category_name: categoryName || 'Category',
        context: aiContext,
        existing_values: existingValues,
        generation_type: aiModalMode // 'options' or 'suggestions'
      });

      if (response.data.success && response.data.values?.length > 0) {
        setAiSuggestions(response.data.values);
        setSelectedSuggestions(response.data.values); // Select all by default
        toast.success(`Generated ${response.data.count} ${aiModalMode === 'suggestions' ? 'suggestions' : 'options'}`);
      } else {
        toast.error('No values generated');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate values');
    } finally {
      setAiLoading(false);
    }
  };

  const toggleSuggestion = (value) => {
    setSelectedSuggestions(prev => 
      prev.includes(value) 
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  };

  const selectAllSuggestions = () => {
    setSelectedSuggestions([...aiSuggestions]);
  };

  const deselectAllSuggestions = () => {
    setSelectedSuggestions([]);
  };

  const addSelectedSuggestions = () => {
    if (selectedSuggestions.length === 0) {
      toast.error('Please select at least one value to add');
      return;
    }
    
    if (aiModalMode === 'options') {
      // Adding to dropdown options
      const currentOptions = property.options || [];
      const newOptions = [...currentOptions, ...selectedSuggestions];
      newOptions.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      onPropertyChange({ ...property, options: newOptions });
      toast.success(`Added ${selectedSuggestions.length} options`);
    } else {
      // Adding to text autocomplete suggestions
      const currentSuggestions = property.suggestions || [];
      const newSuggestions = [...currentSuggestions, ...selectedSuggestions];
      newSuggestions.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      onPropertyChange({ ...property, suggestions: newSuggestions });
      toast.success(`Added ${selectedSuggestions.length} suggestions`);
    }
    
    setShowAIModal(false);
    setAiSuggestions([]);
    setSelectedSuggestions([]);
    setAiContext('');
  };
  
  const openAIModal = (mode) => {
    setAiModalMode(mode);
    setShowAIModal(true);
    setAiContext('');
    setAiSuggestions([]);
    setSelectedSuggestions([]);
  };

  return (
    <div className="p-4 bg-secondary/50 rounded-xl space-y-4 border border-border">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            Property Key <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={property.key || ''}
            onChange={e => handleFieldChange('key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
            placeholder="e.g., mileage, bedrooms"
            className="form-input"
            disabled={isEditing && property.is_system}
          />
          <p className="text-xs text-muted-foreground mt-1">Used internally (no spaces)</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Property Type</label>
          <select
            value={property.type || 'text'}
            onChange={e => handleFieldChange('type', e.target.value)}
            className="form-input"
          >
            {PROPERTY_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          Display Name <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <input type="text" value={property.name?.en || ''} onChange={e => handleNameChange('en', e.target.value)} placeholder="English" className="form-input" />
            <p className="text-xs text-muted-foreground">EN</p>
          </div>
          <div>
            <input type="text" value={property.name?.nl || ''} onChange={e => handleNameChange('nl', e.target.value)} placeholder="Dutch" className="form-input" />
            <p className="text-xs text-muted-foreground">NL</p>
          </div>
          <div>
            <input type="text" value={property.name?.es || ''} onChange={e => handleNameChange('es', e.target.value)} placeholder="Spanish" className="form-input" />
            <p className="text-xs text-muted-foreground">ES</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Group Name</label>
          <input
            type="text"
            value={property.group || ''}
            onChange={e => handleFieldChange('group', e.target.value)}
            placeholder="e.g., Vehicle Details, Amenities"
            className="form-input"
          />
          <p className="text-xs text-muted-foreground">Groups properties in the form</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Sort Order</label>
          <input
            type="number"
            value={property.sort_order || 0}
            onChange={e => handleFieldChange('sort_order', parseInt(e.target.value) || 0)}
            className="form-input"
            min="0"
          />
        </div>
      </div>

      {categorySubcategories?.length > 0 && (
        <div>
          <label className="block text-sm font-medium mb-2">Applies to Subcategories</label>
          <div className="flex flex-wrap gap-2">
            {categorySubcategories?.map(sub => {
              const subId = typeof sub === 'object' ? sub.id : sub;
              const subName = typeof sub === 'object' ? (sub.name?.en || sub.id) : sub;
              return (
              <button
                key={subId}
                type="button"
                onClick={() => toggleSubcategory(subId)}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  (property.subcategories || []).includes(subId)
                    ? 'bg-primary text-white border-primary'
                    : 'bg-secondary border-border hover:border-primary'
                }`}
              >
                {subName}
              </button>
            );})}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {(property.subcategories || []).length === 0 ? 'No selection = applies to ALL' : `Selected: ${property.subcategories.join(', ')}`}
          </p>
        </div>
      )}

      {/* Listing Types Filter */}
      <div>
        <label className="block text-sm font-medium mb-2">Show for Listing Types</label>
        <div className="flex flex-wrap gap-2">
          {LISTING_TYPES.map(lt => (
            <button
              key={lt.value}
              type="button"
              onClick={() => toggleListingType(lt.value)}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                (property.listing_types || []).includes(lt.value)
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-secondary border-border hover:border-blue-500'
              }`}
            >
              {lt.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {(property.listing_types || []).length === 0 
            ? 'No selection = shows for ALL listing types' 
            : `Shows only for: ${(property.listing_types || []).map(t => LISTING_TYPES.find(lt => lt.value === t)?.label || t).join(', ')}`}
        </p>
      </div>

      {availableForDependency.length > 0 && (
        <div className="p-3 bg-background rounded-lg border border-border">
          <label className="block text-sm font-medium mb-2 flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Show only when...
          </label>
          <div className="grid grid-cols-2 gap-3">
            <select
              value={property.depends_on?.property_key || ''}
              onChange={e => handleDependencyChange('property_key', e.target.value)}
              className="form-input text-sm"
            >
              <option value="">No dependency (always show)</option>
              {availableForDependency.map(p => (
                <option key={p.id} value={p.key}>{p.name?.en || p.key}</option>
              ))}
            </select>
            {property.depends_on?.property_key && selectedDepProperty && (
              <div>
                {selectedDepProperty.type === 'boolean' ? (
                  <select value={property.depends_on?.value === true ? 'true' : 'false'} onChange={e => handleDependencyChange('value', e.target.value === 'true')} className="form-input text-sm">
                    <option value="true">is Yes/Checked</option>
                    <option value="false">is No/Unchecked</option>
                  </select>
                ) : (
                  <select value={property.depends_on?.value || ''} onChange={e => handleDependencyChange('value', e.target.value)} className="form-input text-sm">
                    <option value="">Select value...</option>
                    {(selectedDepProperty.options || []).map(opt => (<option key={opt} value={opt}>{opt}</option>))}
                  </select>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {property.type === 'number' && (
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Unit</label>
            <input type="text" value={property.unit || ''} onChange={e => handleFieldChange('unit', e.target.value)} placeholder="e.g., km, m²" className="form-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Min Value</label>
            <input type="number" value={property.min_value ?? ''} onChange={e => handleFieldChange('min_value', e.target.value)} placeholder="Optional" className="form-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Max Value</label>
            <input type="number" value={property.max_value ?? ''} onChange={e => handleFieldChange('max_value', e.target.value)} placeholder="Optional" className="form-input" />
          </div>
        </div>
      )}

      {property.type === 'predefined_list' && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium">Options</label>
            <button
              type="button"
              onClick={() => openAIModal('options')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Generate with AI
            </button>
          </div>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={newOption}
              onChange={e => setNewOption(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addOption())}
              placeholder="Add an option..."
              className="form-input flex-1"
            />
            <button type="button" onClick={addOption} className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90">
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            {(property.options || []).map((opt, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-lg text-sm">
                {opt}
                <button type="button" onClick={() => removeOption(idx)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          {(property.options || []).length === 0 && (
            <p className="text-sm text-amber-500 flex items-center gap-1 mt-2"><AlertCircle className="w-4 h-4" /> Add at least one option</p>
          )}
          {(property.options || []).length > 0 && (
            <p className="text-xs text-muted-foreground mt-2">{property.options.length} options added</p>
          )}
        </div>
      )}

      {/* Suggestions for text inputs (autocomplete) */}
      {property.type === 'text' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-500" />
              Autocomplete Suggestions
              <span className="text-xs text-muted-foreground font-normal">(helps users avoid typos)</span>
            </label>
            <button
              type="button"
              onClick={() => openAIModal('suggestions')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Generate with AI
            </button>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newSuggestion || ''}
              onChange={e => setNewSuggestion(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSuggestion(); } }}
              placeholder="Type a suggestion and press Enter"
              className="form-input flex-1"
            />
            <button
              type="button"
              onClick={addSuggestion}
              className="p-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            {(property.suggestions || []).map((sug, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded-lg text-sm">
                {sug}
                <button type="button" onClick={() => removeSuggestion(idx)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          {(property.suggestions || []).length > 0 && (
            <p className="text-xs text-muted-foreground">{property.suggestions.length} suggestions • Users will see these when typing</p>
          )}
        </div>
      )}

      {/* AI Generation Modal */}
      {showAIModal && (
        <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowAIModal(false)}>
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                <h3 className="font-semibold text-foreground">
                  {aiModalMode === 'suggestions' ? 'AI Suggestion Generator' : 'AI Option Generator'}
                </h3>
              </div>
              <button onClick={() => setShowAIModal(false)} className="p-2 hover:bg-secondary rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Context Input */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  {aiModalMode === 'suggestions' 
                    ? 'Describe what autocomplete suggestions you need' 
                    : 'Describe what options you need'}
                </label>
                <textarea
                  value={aiContext}
                  onChange={e => setAiContext(e.target.value)}
                  placeholder={aiModalMode === 'suggestions'
                    ? `Example: "Common car model names like Corolla, Civic, Golf" or "Popular color names for vehicles"`
                    : `Example: "I need a list of popular car brands worldwide" or "Generate common colors for fashion items"`}
                  className="form-input w-full h-24 resize-none"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Property: <span className="font-medium">{property.name?.en || property.key}</span> in <span className="font-medium">{categoryName}</span>
                  {aiModalMode === 'suggestions' && (
                    <span className="ml-2 text-purple-500">• For text input autocomplete</span>
                  )}
                </p>
              </div>

              {/* Generate Button */}
              <button
                onClick={generateWithAI}
                disabled={aiLoading || !aiContext.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl font-medium hover:from-purple-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {aiLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate {aiModalMode === 'suggestions' ? 'Suggestions' : 'Options'}
                  </>
                )}
              </button>

              {/* Suggestions List */}
              {aiSuggestions.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">
                      {aiSuggestions.length} {aiModalMode === 'suggestions' ? 'suggestions' : 'options'} generated
                    </p>
                    <div className="flex gap-2">
                      <button onClick={selectAllSuggestions} className="text-xs text-primary hover:underline">Select All</button>
                      <span className="text-muted-foreground">|</span>
                      <button onClick={deselectAllSuggestions} className="text-xs text-primary hover:underline">Deselect All</button>
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto border border-border rounded-xl p-3 space-y-1">
                    {aiSuggestions.map((suggestion, idx) => (
                      <label key={idx} className="flex items-center gap-2 p-2 hover:bg-secondary rounded-lg cursor-pointer">
                        <div 
                          onClick={(e) => { e.preventDefault(); toggleSuggestion(suggestion); }}
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all cursor-pointer flex-shrink-0 ${
                            selectedSuggestions.includes(suggestion) 
                              ? 'bg-primary border-primary' 
                              : 'border-muted-foreground/50 hover:border-primary/50'
                          }`}
                        >
                          {selectedSuggestions.includes(suggestion) && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="text-sm">{suggestion}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {selectedSuggestions.length} of {aiSuggestions.length} selected
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            {aiSuggestions.length > 0 && (
              <div className="p-4 border-t border-border">
                <button
                  onClick={addSelectedSuggestions}
                  disabled={selectedSuggestions.length === 0}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check className="w-5 h-5" />
                  Add {selectedSuggestions.length} Selected {aiModalMode === 'suggestions' ? 'Suggestions' : 'Options'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {property.type !== 'boolean' && (
        <div>
          <label className="block text-sm font-medium mb-1">Placeholder Text</label>
          <div className="grid grid-cols-3 gap-2">
            <input type="text" value={property.placeholder?.en || ''} onChange={e => handlePlaceholderChange('en', e.target.value)} placeholder="English" className="form-input" />
            <input type="text" value={property.placeholder?.nl || ''} onChange={e => handlePlaceholderChange('nl', e.target.value)} placeholder="Dutch" className="form-input" />
            <input type="text" value={property.placeholder?.es || ''} onChange={e => handlePlaceholderChange('es', e.target.value)} placeholder="Spanish" className="form-input" />
          </div>
        </div>
      )}

      <div className="flex gap-6">
        <label className="flex items-center gap-2 cursor-pointer group">
          <div 
            onClick={() => handleFieldChange('required', !property.required)}
            className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all cursor-pointer ${
              property.required 
                ? 'bg-primary border-primary' 
                : 'border-muted-foreground/50 hover:border-primary/50'
            }`}
          >
            {property.required && (
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <span className="text-sm">Required field</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer group">
          <div 
            onClick={() => handleFieldChange('filterable', !property.filterable)}
            className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all cursor-pointer ${
              property.filterable 
                ? 'bg-primary border-primary' 
                : 'border-muted-foreground/50 hover:border-primary/50'
            }`}
          >
            {property.filterable && (
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <span className="text-sm">Show as filter</span>
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-border">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">Cancel</button>
        <button type="button" onClick={onSave} className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2">
          <Check className="w-4 h-4" /> {isEditing ? 'Update' : 'Add'}
        </button>
      </div>
    </div>
  );
};

// Main Component
const CategoryPropertyManager = ({ category, onClose }) => {
  const { token } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPropertyId, setEditingPropertyId] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [filterSubcategory, setFilterSubcategory] = useState('all');
  const [newOption, setNewOption] = useState('');
  const [newSuggestion, setNewSuggestion] = useState('');
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'flat'
  
  const [newProperty, setNewProperty] = useState({
    key: '', name: { en: '', nl: '', es: '' }, type: 'text', required: false, filterable: false,
    options: [], suggestions: [], placeholder: { en: '', nl: '', es: '' }, unit: '', min_value: '', max_value: '',
    group: '', subcategories: [], listing_types: [], depends_on: null,
  });

  useEffect(() => { fetchProperties(); }, [category.id]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/categories/${category.id}/properties`);
      const sorted = (response.data.properties || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setProperties(sorted);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const resetNewProperty = () => {
    setNewProperty({
      key: '', name: { en: '', nl: '', es: '' }, type: 'text', required: false, filterable: false,
      options: [], suggestions: [], placeholder: { en: '', nl: '', es: '' }, unit: '', min_value: '', max_value: '',
      group: '', subcategories: [], listing_types: [], depends_on: null,
    });
    setNewOption('');
    setNewSuggestion('');
  };

  const handleAddProperty = async () => {
    if (!newProperty.key || !newProperty.name.en) {
      toast.error('Property key and English name are required');
      return;
    }
    try {
      const payload = {
        ...newProperty,
        sort_order: properties.length,
        min_value: newProperty.min_value !== '' ? parseFloat(newProperty.min_value) : null,
        max_value: newProperty.max_value !== '' ? parseFloat(newProperty.max_value) : null
      };
      const response = await axios.post(`${API}/admin/categories/${category.id}/properties`, payload);
      setProperties([...properties, response.data.property]);
      setShowAddForm(false);
      resetNewProperty();
      toast.success('Property added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add property');
    }
  };

  const handleUpdateProperty = async () => {
    if (!editingProperty) return;
    try {
      const payload = {
        ...editingProperty,
        min_value: editingProperty.min_value !== '' && editingProperty.min_value !== null ? parseFloat(editingProperty.min_value) : null,
        max_value: editingProperty.max_value !== '' && editingProperty.max_value !== null ? parseFloat(editingProperty.max_value) : null
      };
      const response = await axios.put(`${API}/admin/categories/${category.id}/properties/${editingProperty.id}`, payload);
      setProperties(properties.map(p => p.id === editingProperty.id ? response.data.property : p));
      setEditingPropertyId(null);
      setEditingProperty(null);
      toast.success('Property updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update property');
    }
  };

  const handleDeleteProperty = async (propertyId, propertyName) => {
    if (!confirm(`Delete "${propertyName}"?`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${category.id}/properties/${propertyId}`);
      setProperties(properties.filter(p => p.id !== propertyId));
      toast.success('Property deleted');
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  // Move property up or down in the DISPLAYED list
  const moveProperty = async (displayedList, propId, direction) => {
    const currentDisplayIndex = displayedList.findIndex(p => p.id === propId);
    if (currentDisplayIndex === -1) return;
    
    const newDisplayIndex = direction === 'up' ? currentDisplayIndex - 1 : currentDisplayIndex + 1;
    if (newDisplayIndex < 0 || newDisplayIndex >= displayedList.length) return;

    // Get the two properties being swapped
    const prop1 = displayedList[currentDisplayIndex];
    const prop2 = displayedList[newDisplayIndex];

    // Swap their sort_order values
    const newSortOrder1 = prop2.sort_order;
    const newSortOrder2 = prop1.sort_order;

    // Update in the main properties array
    const newProperties = properties.map(p => {
      if (p.id === prop1.id) return { ...p, sort_order: newSortOrder1 };
      if (p.id === prop2.id) return { ...p, sort_order: newSortOrder2 };
      return p;
    });

    // Sort by sort_order
    newProperties.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    setProperties(newProperties);

    // Save to backend
    try {
      await axios.put(
        `${API}/admin/categories/${category.id}/properties/reorder`,
        { property_ids: newProperties.map(p => p.id) });
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Failed to save order');
      fetchProperties();
    }
  };

  // Filter properties
  const filteredProperties = properties.filter(prop => {
    if (filterSubcategory === 'all') return true;
    const propSubs = prop.subcategories || [];
    return propSubs.length === 0 || propSubs.includes(filterSubcategory);
  });

  // Group properties
  const groupedProperties = filteredProperties.reduce((acc, prop) => {
    const group = prop.group || 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(prop);
    return acc;
  }, {});

  const getDependencyText = (prop) => {
    if (!prop.depends_on?.property_key) return null;
    const depProp = properties.find(p => p.key === prop.depends_on.property_key);
    if (!depProp) return null;
    return depProp.type === 'boolean' 
      ? `if "${depProp.name?.en}" is ${prop.depends_on.value ? 'Yes' : 'No'}`
      : `if "${depProp.name?.en}" = "${prop.depends_on.value}"`;
  };

  const renderPropertyRow = (prop, list, index) => {
    const canUp = index > 0;
    const canDown = index < list.length - 1;

    if (editingPropertyId === prop.id) {
      return (
        <PropertyForm
          key={prop.id}
          property={editingProperty}
          onPropertyChange={setEditingProperty}
          onSave={handleUpdateProperty}
          onCancel={() => { setEditingPropertyId(null); setEditingProperty(null); }}
          isEditing={true}
          categorySubcategories={category.subcategories}
          allProperties={properties}
          newOption={newOption}
          setNewOption={setNewOption}
          newSuggestion={newSuggestion}
          setNewSuggestion={setNewSuggestion}
          categoryName={category.name?.en || category.name || 'Category'}
          token={token}
        />
      );
    }

    return (
      <div key={prop.id} className="flex items-center gap-2 p-3 bg-background rounded-lg border border-border/50 hover:border-border transition-colors">
        <div className="flex flex-col gap-0.5">
          <button
            onClick={() => moveProperty(list, prop.id, 'up')}
            disabled={!canUp}
            className={`p-1 rounded ${canUp ? 'hover:bg-secondary text-muted-foreground hover:text-foreground' : 'text-muted-foreground/30 cursor-not-allowed'}`}
          >
            <ArrowUp className="w-3 h-3" />
          </button>
          <button
            onClick={() => moveProperty(list, prop.id, 'down')}
            disabled={!canDown}
            className={`p-1 rounded ${canDown ? 'hover:bg-secondary text-muted-foreground hover:text-foreground' : 'text-muted-foreground/30 cursor-not-allowed'}`}
          >
            <ArrowDown className="w-3 h-3" />
          </button>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{prop.name?.en || prop.key}</span>
            <span className="text-xs px-2 py-0.5 bg-blue-500/10 text-blue-500 rounded">{PROPERTY_TYPES.find(t => t.value === prop.type)?.label || prop.type}</span>
            {prop.required && <span className="text-xs px-2 py-0.5 bg-red-500/10 text-red-500 rounded">Required</span>}
            {prop.filterable && <span className="text-xs px-2 py-0.5 bg-green-500/10 text-green-500 rounded">Filter</span>}
            {prop.is_system && <span className="text-xs px-2 py-0.5 bg-purple-500/10 text-purple-500 rounded">System</span>}
          </div>
          <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-2">
            <span>Key: {prop.key}</span>
            {prop.group && <span>• Group: {prop.group}</span>}
            {prop.unit && <span>• Unit: {prop.unit}</span>}
            {prop.subcategories?.length > 0 && <span>• Subcats: {prop.subcategories.join(', ')}</span>}
            {prop.listing_types?.length > 0 && <span className="text-blue-500">• Types: {prop.listing_types.map(t => LISTING_TYPES.find(lt => lt.value === t)?.label || t).join(', ')}</span>}
            {getDependencyText(prop) && <span className="text-amber-500">• Shows {getDependencyText(prop)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => { setEditingPropertyId(prop.id); setEditingProperty({ ...prop }); }} className="p-2 hover:bg-secondary rounded-lg text-muted-foreground hover:text-foreground">
            <Edit2 className="w-4 h-4" />
          </button>
          <button onClick={() => handleDeleteProperty(prop.id, prop.name?.en || prop.key)} className="p-2 hover:bg-red-500/10 rounded-lg text-muted-foreground hover:text-red-500">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[150] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">Category Properties</h3>
              <p className="text-sm text-muted-foreground">
                Manage fields for <span className="text-primary font-medium">{category.name?.en || category.id}</span>
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg"><X className="w-5 h-5" /></button>
          </div>
          
          <div className="mt-4 flex items-center justify-between gap-4">
            {category.subcategories?.length > 0 && (
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <select value={filterSubcategory} onChange={e => setFilterSubcategory(e.target.value)} className="form-input py-1 px-2 text-sm w-auto">
                  <option value="all">All Subcategories</option>
                  {category.subcategories.map(sub => {
                    const subId = typeof sub === 'object' ? sub.id : sub;
                    const subName = typeof sub === 'object' ? (sub.name?.en || sub.id) : sub;
                    return (<option key={subId} value={subId}>{subName}</option>);
                  })}
                </select>
              </div>
            )}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('flat')}
                className={`px-3 py-1 text-sm rounded-lg ${viewMode === 'flat' ? 'bg-primary text-white' : 'bg-secondary'}`}
              >
                Flat List
              </button>
              <button
                onClick={() => setViewMode('grouped')}
                className={`px-3 py-1 text-sm rounded-lg ${viewMode === 'grouped' ? 'bg-primary text-white' : 'bg-secondary'}`}
              >
                Grouped
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : (
            <div className="space-y-4">
              {filteredProperties.length === 0 && !showAddForm ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Tag className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No properties defined yet.</p>
                </div>
              ) : viewMode === 'flat' ? (
                // Flat view - simple list sorted by sort_order
                <div className="space-y-2">
                  {filteredProperties.map((prop, index) => renderPropertyRow(prop, filteredProperties, index))}
                </div>
              ) : (
                // Grouped view
                Object.entries(groupedProperties).map(([groupName, groupProps]) => (
                  <div key={groupName} className="border border-border rounded-xl overflow-hidden">
                    <div className="p-3 bg-secondary/30 font-medium flex items-center gap-2">
                      {groupName}
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{groupProps.length}</span>
                    </div>
                    <div className="p-3 space-y-2">
                      {groupProps.map((prop, index) => renderPropertyRow(prop, groupProps, index))}
                    </div>
                  </div>
                ))
              )}

              {showAddForm ? (
                <PropertyForm
                  property={newProperty}
                  onPropertyChange={setNewProperty}
                  onSave={handleAddProperty}
                  onCancel={() => { setShowAddForm(false); resetNewProperty(); }}
                  isEditing={false}
                  categorySubcategories={category.subcategories}
                  allProperties={properties}
                  newOption={newOption}
                  setNewOption={setNewOption}
                  newSuggestion={newSuggestion}
                  setNewSuggestion={setNewSuggestion}
                  categoryName={category.name?.en || category.name || 'Category'}
                  token={token}
                />
              ) : (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-border rounded-xl text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                >
                  <Plus className="w-5 h-5" /> Add New Property
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-secondary/30 text-sm text-muted-foreground">
          <div className="flex justify-between">
            <span>{properties.length} properties</span>
            <span>{properties.filter(p => p.required).length} required • {properties.filter(p => p.filterable).length} filterable</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryPropertyManager;
